import { gql } from '@apollo/client';
import { TRANSLATED_ENTRY_FRAGMENT } from '../../../../../common/graphql/translatedEntry/translated-entry-graphql';
import { TranslatedEntry } from '../../../../../common/i18n';
import { Revenue } from '../../Admin/Developer/developer-games-graphql';
import { GAME_STATUS } from '@crazygames/game-types/src/game/game-status.type';
import { GameAdvertisementStats } from '@crazygames/graph-types/game/game-ad-stats.type';
import { DeveloperGameAnalytics } from '@crazygames/graph-types/game/developer-analytics.type';
import { DeveloperAnalyticsBenchmarks } from '@crazygames/graph-types/game/developer-analytics-benchmarks.type';
import { Submission as DeveloperSubmission } from '../Submission/submission-graphql';

/**
 * Type for lists of submissions
 */
export type SubmissionForList = Pick<
  DeveloperSubmission,
  'id' | 'slug' | 'createdAt' | 'gameName' | 'gameStatus' | 'status' | 'isFullLaunch' | 'feedback' | 'gameCovers'
>;

export interface Review {
  comment: string;
  postedOn: string;
  authorName: string;
}

export interface MySubmissionsQuery {
  me: {
    submissions: SubmissionForList[];
  };
}

export const SUBMISSIONS_FRAGMENT = gql`
  fragment SubmissionsFragment on Developer {
    submissions {
      id
      gameName
      gameStatus
      isFullLaunch
      createdAt
      status
      slug
      gameCovers {
        size
        uploadedFile {
          uploadId
          path
          size
          temporaryUrl
        }
      }
    }
  }
`;

export const MY_SUBMISSIONS_QUERY = gql`
  ${SUBMISSIONS_FRAGMENT}
  query {
    me {
      id
      ...SubmissionsFragment
    }
  }
`;

export interface DeveloperDailyRevenueStat {
  date: string;
  /** Revenue share ordered from most recent to oldest */
  revenueShareDaily: { total: number; iap: number; admob: number };
}

export interface DeveloperGame {
  slug: string;
  id: string;
  name: TranslatedEntry[];
  status: GAME_STATUS;
  crazygamesUrl: string;
  devStats: DeveloperGameStatsForTimespan;
  dailyRevenueStats: DeveloperDailyRevenueStat[];
  coverUrl: string;
}

export interface DeveloperGameStatsForTimespan {
  from: string;
  until: string;
  revenueShare: Revenue;
  analytics: DeveloperGameAnalytics;
  benchmarks: DeveloperAnalyticsBenchmarks;
  advertisementStats: GameAdvertisementStats;
}

export interface MyGamesStatsQuery {
  me: {
    games: DeveloperGame[];
  };
}

export const GAMES_STATS_FRAGMENT = gql`
  fragment GamesStatsFragment on Developer {
    games {
      id
      slug
      name {
        ...TranslatedEntryFragment
      }
      status
      coverUrl
      crazygamesUrl
      devStats(period: $period, options: $options) {
        from
        until
        revenueShare {
          total
        }
        analytics {
          pageviews
          users
          avgTimeOnPage
          rating
          totalVotes
          retentionD1
          retentionD7
          returningPlayers
          avgConversion
          avgLoadingTime
          ctr
          issues
        }
      }
    }
  }
`;

export const MY_GAMES_STATS_QUERY = gql`
  ${GAMES_STATS_FRAGMENT}
  query gameStats($period: StatsForPeriod!, $options: StatsForPeriodOptionsInput) {
    me {
      id
      ...GamesStatsFragment
    }
  }
  ${TRANSLATED_ENTRY_FRAGMENT}
`;
